import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { getUserInfo } from '@/features/auth/services/auth';
import type { UserInfoResponse } from '@/features/auth/types/auth.types';
import { getAuthSession } from '@/shared/api/storage';

/**
 * Hook for fetching authenticated user information
 *
 * Automatically fetches user data on mount and caches it.
 * Requires valid JWT token in the auth store.
 * Uses token expiresIn as staleTime to align cache with token lifetime.
 *
 * @example
 * ```tsx
 * const { data: userInfo, isLoading, error } = useUserInfo();
 *
 * if (isLoading) return <Spinner />;
 * if (error) return <Error />;
 *
 * return <div>Welcome, {userInfo.name}!</div>;
 * ```
 */
export function useUserInfo() {
  const authData = getAuthSession();
  const staleTime = authData?.expiresIn
    ? Number(authData.expiresIn) * 1000
    : 5 * 60 * 1000;

  return useQuery<UserInfoResponse, Error>({
    queryKey: ['userInfo'],
    queryFn: () => getUserInfo(),
    staleTime,
    // 4xx são determinísticos: repetir não muda a resposta e, no caso do
    // acesso federado não liberado (CA11), duplicaria o evento AUTHZ_DENIED
    // na auditoria (RN09). Falhas transitórias (rede/5xx) seguem com 1 retry.
    retry: (failureCount, error) => {
      const status = axios.isAxiosError(error)
        ? error.response?.status
        : undefined;
      if (status && status >= 400 && status < 500) return false;
      return failureCount < 1;
    },
  });
}
